/**
 * PtsOutputs virtual views: seven de-prefixed, ancestry-enriched surfaces
 * fronting the ~56-column `PtsOutputs_String_Agent` source (PTS
 * pressure/temperature survey fluid outputs, string-grain, StringContext).
 *
 * The source carries the per-string PTS well-test fluid outputs as a single
 * wide row keyed by the `wellString` producing-string relation. Most measures
 * are sampled at three PTS conditions whose markers are TRAILING suffixes — not
 * leading prefixes: `…Pts1` (line conditions), `…Pts2` (standard conditions),
 * and `…PtsRef` (PTS reference conditions). Because the de-prefix axis is a
 * trailing marker, the EstimatedRates-style leading-`prefix` split does NOT
 * apply: each per-sample shape carries the full verbatim source tail in its
 * `suffix` and the de-marked name in `canonical`, applied with the default
 * empty prefix. A further block of measures is un-sampled (no `Pts*` marker):
 * the bare fluid composition + sample metadata and the calculated/context GOR
 * and gas-lift columns, each kept once.
 *
 * Each view here slices the wide row to one concern (a sample's rates, a
 * sample's fluid properties, the bare composition, or the bare calc/context
 * measures), de-marks the family to canonical names via the shared shapes,
 * keeps identity + the wellString relation, and enriches each row with its
 * well / field / reservoir ancestry resolved through the FlatHierarchy
 * registry.
 *
 * Built per session (not module-level) because the enriched build closes over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import {
  PTS_CALC_CONTEXT,
  PTS_FLUID_COMPOSITION,
  PTS_FLUID_PROPERTIES_PTS1,
  PTS_FLUID_PROPERTIES_PTS2,
  PTS_RATES_PTS1,
  PTS_RATES_PTS2,
  PTS_RATES_REF,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { PtsOutputsStringAgentView } from '#schema/generated/ipso/views.ts';

/** StringContext: well/field/reservoir ancestry off the `wellString` ref. */

/** One concern-scoped PtsOutputs string surface: a canonical name, agent doc, and the de-marked groups to keep. */
type PtsOutputsSpec = ViewFamilySpec;

/**
 * The seven PtsOutputs subviews, declared as data. Each de-marks one concern of
 * the PTS well-test measure family; all share identity + the wellString
 * relation and the well/field/reservoir ancestry enrichment.
 *
 * The per-sample rate/property shapes carry the trailing `Pts1`/`Pts2`/`PtsRef`
 * marker inside their suffixes (applied with the default empty prefix), so the
 * groups omit any prefix; the bare composition and calc/context shapes are
 * likewise un-prefixed (suffix === source column).
 */
const PTS_OUTPUTS_SPECS: readonly PtsOutputsSpec[] = [
  {
    name: 'WellTestPtsRatesLine',
    description: `Per-string PTS well-test production rates at line conditions (oil/gas/water/liquid rate, water cut, GOR, WGR, solution GOR/CGR)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: PTS_RATES_PTS1 }],
  },
  {
    name: 'WellTestPtsRatesStandard',
    description: `Per-string PTS well-test production rates at standard conditions (oil/gas/water/liquid rate, water cut, GOR)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: PTS_RATES_PTS2 }],
  },
  {
    name: 'WellTestPtsRatesReference',
    description: `Per-string PTS well-test production rates at the PTS reference conditions (oil/liquid rate, water cut, GOR, CGR, free GOR/CGR)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: PTS_RATES_REF }],
  },
  {
    name: 'PtsFluidPropertiesLine',
    description: `Per-string PTS fluid properties at line conditions (oil/gas/water density, gas specific gravity, Bo/Bg formation volume factors, fluid composition)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: PTS_FLUID_PROPERTIES_PTS1 }],
  },
  {
    name: 'PtsFluidPropertiesStandard',
    description: `Per-string PTS fluid properties at standard conditions (oil/gas/water density, gas specific gravity, Bo/Bg formation volume factors)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: PTS_FLUID_PROPERTIES_PTS2 }],
  },
  {
    name: 'PtsComposition',
    description: `Per-string PTS fluid composition and sample metadata (CO2/H2S/N2 mol fractions, gas gravity, water salinity, raw composition, saturation pressure, solution GOR/CGR, sample timestamp)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: PTS_FLUID_COMPOSITION }],
  },
  {
    name: 'PtsCalcContext',
    description: `Per-string calculated and context PTS measures (calculated/in-situ/total well-test GOR, SC free GOR, well-test GOR, estimated/used gas-lift injection and well-test gas-lift rates)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: PTS_CALC_CONTEXT }],
  },
];

/**
 * Build the seven de-marked, ancestry-enriched PtsOutputs surfaces, each a
 * StringContext view over the shared source + well/field/reservoir enrichment;
 * filter by wellString (or join WellEntity) to scope by hierarchy.
 */
export const usePtsOutputsViews = makeStringContextViews({
  source: () => PtsOutputsStringAgentView,
  specs: PTS_OUTPUTS_SPECS,
});
